import type { ChangeEvent, FormEvent } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import type React from 'react';
import classNames from 'classnames';
import { shallowEqual } from 'react-redux';
import styles from './PriceOverview.module.scss';
import TotalPrice from './TotalPrice';
import FormatPrice from '../../../general/Format/FormatPrice';
import { useTypedSelector, useTypedDispatch } from '../../Store/connectors';
import Translation from '../../../general/Translation/Translation';
import { addDiscountCode, resetDiscountCode, resetVoucherCode } from '../../Store/Prices/action';
import Discount from './Discount';
import Icon from '../../../../stories/Atoms/Icon/Icon';
import { useWithImageUrl } from '../../../general/WebshopContext/WebshopContext';
import ContinueToCheckout from '../Cta/ContinueToCheckout';
import ShoppingAward from '../../../general/ShoppingAward/ShoppingAward';
import { WebshopContext } from '../../../general/WebshopContext/WebshopContextProvider';
import { cartDeliveryFrom } from '../../../../js/optimizely/fx/flags';
import type { Price } from '../../../../ssr/src/Outlet/ShoppingCart/ShoppingCartSchema';

export interface PriceOverviewProps {
    shippingPrice: Price;
    subTotal: Price;
    total: Price;
}

const PriceOverview: React.FC<PriceOverviewProps> = ({ shippingPrice, subTotal, total }) => {
    const dispatch = useTypedDispatch();
    const addDiscount = (code: string) => dispatch(addDiscountCode({ code }));
    const deleteDiscount = () => dispatch(resetDiscountCode());
    const deleteVoucher = () => dispatch(resetVoucherCode());
    const withImageUrl = useWithImageUrl();

    const context = useContext(WebshopContext);
    const isVariationB = context.experimentVariation === cartDeliveryFrom.variationB;

    const [
        discount,
        discountCode,
        hasDiscount,
        isDiscountCodeValid,
        specialDiscountPeriod,
        paymentMethods,
        showShoppingAward,
        userHasSelectedShippingMethod,
        voucher,
        voucherCode,
    ] = useTypedSelector(
        ({ metaData, prices, ui }): [Price, string, boolean, boolean, boolean, string[], boolean, boolean, Price, string] => [
            prices.discount,
            prices.discountCode,
            prices.hasDiscount,
            prices.isDiscountCodeValid,
            metaData.specialDiscountPeriod,
            metaData.paymentMethods,
            metaData.showShoppingAward,
            ui.userHasSelectedShippingMethod,
            prices.voucher,
            prices.voucherCode,
        ],
        shallowEqual,
    );

    const [showInput, setShowInput] = useState(false);
    const [activeDiscountCode, setDiscountCode] = useState(discountCode);
    const discountInputRef = useRef<HTMLInputElement>(null);
    const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        setDiscountCode(e.target.value);
    };

    useEffect(() => {
        if (discountInputRef.current && showInput) {
            discountInputRef.current.focus();
        }
    }, [discountInputRef, showInput]);

    useEffect(() => setShowInput(false), [hasDiscount]);

    const saveDiscountCode = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        addDiscount(activeDiscountCode);
    };

    const discountInputFiled = (
        <form className={classNames(styles.PriceOverview__discountForm, 'form')} onSubmit={saveDiscountCode}>
            <div className="form-row">
                <div className="form-control">
                    <input
                        id="discount"
                        className={classNames('form-input', { dirty: discountInputRef.current?.value })}
                        value={activeDiscountCode}
                        onChange={changeHandler}
                        name="discount"
                        ref={discountInputRef}
                    />
                    <label className="form-label" htmlFor="discount">
                        <Translation pageString="winkelmand_teksten" stringId="kortingscode"/>
                    </label>
                </div>
                {!isDiscountCodeValid && (
                    <ul className="form-error">
                        <li className="error">
                            <Icon name='fa-circle-exclamation' />
                            <Translation pageString="checkout" stringId="discount-code-invalid"/>
                        </li>
                    </ul>
                )}
            </div>
            <div className="form-row form-row--flex form-row--end">
                <button type="submit" className="button button--ghost button--xs" name="discount-add">
                    <span>
                        <Translation pageString="class.bestelproces" stringId="toevoegen"/>
                    </span>
                </button>
            </div>
        </form>
    );

    return (
        <div className="is-col-1">
            <div className={styles.PriceOverview}>
                <div className={styles.PriceOverview__container}>
                    <p className="h3"><Translation pageString="checkout" stringId="summary-title"/></p>
                    <div className={styles.PriceOverview__content}>
                        <div className={styles.PriceOverview__row}>
                            <p><Translation pageString="checkout" stringId="subtotal"/></p>
                            <p translate="no"><FormatPrice price={subTotal}/></p>
                        </div>
                        <div className={classNames(styles.PriceOverview__row)}>
                            <p><Translation pageString="class.bestelproces" stringId="verzendkosten"/></p>
                            <p translate="no">
                                {!userHasSelectedShippingMethod && shippingPrice.value > 0 && !isVariationB && (
                                    <span className={styles.PriceOverview__row__shippingCosts__priceFrom}>
                                        <Translation pageString="checkout" stringId="price_from"/>
                                        {' '}
                                    </span>
                                )}
                                <FormatPrice price={shippingPrice}/>
                            </p>
                        </div>
                        {!!voucherCode && <Discount code={voucherCode} deleteDiscount={deleteVoucher} discount={voucher} />}
                        {!!discountCode && <Discount code={discountCode} deleteDiscount={deleteDiscount} discount={discount} />}
                        <div>
                            <TotalPrice price={total}/>
                        </div>
                        <ContinueToCheckout/>
                        <div className={styles.PriceOverview__payment}>
                            {paymentMethods.map((d) => (
                                <img
                                    key={d}
                                    loading="lazy"
                                    src={withImageUrl(`/template/payment/payment-${d}@2x.png`)}
                                    alt={d}
                                    height="20"/>
                            ))}
                        </div>
                    </div>
                </div>
                {!hasDiscount && !specialDiscountPeriod && (
                    <div className={styles.PriceOverview__discount}>
                        <button
                            type="button"
                            onClick={() => setShowInput(!showInput)}
                            className={styles['PriceOverview__discount-button']}
                            name="discount-toggle"
                        >
                            <Translation pageString="checkout" stringId="your-discount-code"/>
                            <Icon name={showInput ? 'fa-chevron-up' : 'fa-chevron-down'} />
                        </button>
                        {showInput && discountInputFiled}
                    </div>
                )}
                {showShoppingAward && <ShoppingAward showOnDesktop={false} showOnMobile />}
            </div>
        </div>
    );
};

export default PriceOverview;
